import styled from 'styled-components';
import {graphql, useFragment} from 'react-relay';
import type {WatchersList_ticket$key} from './__generated__/WatchersList_ticket.graphql';
import type {ticketingPerson_person$key} from '../../../graphql/fragments/__generated__/ticketingPerson_person.graphql';
import {ticketingPersonFragment, personDisplayName} from '../../../graphql/fragments/ticketingPerson';
import {useWatchTicket} from '../hooks/useWatchTicket';

const WatchersListFragment = graphql`
	fragment WatchersList_ticket on Ticket {
		id
		isWatching
		watcherCount
		watchers {
			id
			person {
				...ticketingPerson_person
			}
		}
	}
`;

interface Props {
	ticket: WatchersList_ticket$key;
}

// Tailwind default 100/700 avatar palette — no DS token, preserve rendered hex.
const AVATAR_COLORS = [
	{bg: '#dbeafe', fg: '#1d4ed8'},
	{bg: '#f3e8ff', fg: '#7e22ce'},
	{bg: '#dcfce7', fg: '#15803d'},
	{bg: '#fef3c7', fg: '#b45309'},
];

const Section = styled.section`
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	padding: 16px;
	box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
`;
const Header = styled.div`
	margin-bottom: 8px;
	display: flex;
	align-items: center;
	justify-content: space-between;
`;
const Title = styled.span`
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 600;
	color: #0f172a;
`;
const Count = styled.span`
	margin-left: 6px;
	font: ${p => p.theme.ticketing.font.subheading2};
	color: #64748b;
`;
const ToggleButton = styled.button<{$watching: boolean}>`
	border-radius: 6px;
	padding: 4px 10px;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	cursor: pointer;
	border: 1px solid ${p => (p.$watching ? '#bfdbfe' : '#e2e8f0')};
	background: ${p => (p.$watching ? '#eff6ff' : 'transparent')};
	color: ${p => (p.$watching ? '#1d4ed8' : '#475569')};
	&:hover {
		background: ${p => (p.$watching ? '#dbeafe' : '#f8fafc')};
	}
`;
const Description = styled.p`
	font: ${p => p.theme.ticketing.font.body3};
	color: #94a3b8;
	margin: 0;
`;
const ListWrap = styled.div`
	margin-top: 12px;
	min-height: 2rem;
`;
const List = styled.ul`
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 8px;
`;
const Row = styled.li`
	display: flex;
	align-items: center;
	gap: 10px;
	font: ${p => p.theme.ticketing.font.subheading2};
	color: #334155;
`;
const Avatar = styled.span<{$bg: string; $fg: string}>`
	display: flex;
	height: 28px;
	width: 28px;
	align-items: center;
	justify-content: center;
	border-radius: ${p => p.theme.ticketing.radius.full};
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 600;
	background: ${p => p.$bg};
	color: ${p => p.$fg};
`;
const Name = styled.span`
	font-weight: 500;
`;

function WatcherRow({person, color}: {person: ticketingPerson_person$key | null | undefined; color: {bg: string; fg: string}}) {
	const data = useFragment(ticketingPersonFragment, person ?? null);
	const name = data ? personDisplayName(data) : 'Unknown';
	return (
		<Row>
			<Avatar $bg={color.bg} $fg={color.fg}>
				{name.charAt(0).toUpperCase()}
			</Avatar>
			<Name>{name}</Name>
		</Row>
	);
}

export function WatchersList({ticket}: Props) {
	const data = useFragment<WatchersList_ticket$key>(WatchersListFragment, ticket);
	const {watch, unwatch, inFlight} = useWatchTicket();

	const handleToggle = () => {
		if (data.isWatching) {
			unwatch(data.id);
		} else {
			watch(data.id);
		}
	};

	return (
		<Section>
			<Header>
				<Title>
					Watchers
					{data.watcherCount > 0 && <Count>{data.watcherCount}</Count>}
				</Title>
				<ToggleButton type="button" onClick={handleToggle} disabled={inFlight} $watching={data.isWatching}>
					{inFlight ? '…' : data.isWatching ? 'Stop Watching' : 'Watch'}
				</ToggleButton>
			</Header>
			<Description>Watchers get notified when status, assignee, priority, or comments change.</Description>
			<ListWrap>
				{data.watchers.length > 0 && (
					<List>
						{data.watchers.map((w, i) => (
							<WatcherRow key={w.id} person={w.person} color={AVATAR_COLORS[i % AVATAR_COLORS.length]!} />
						))}
					</List>
				)}
			</ListWrap>
		</Section>
	);
}
